/**
 * useLanguage — the single, reusable hook for reading/changing the UI language.
 *
 * Wraps react-i18next so components never touch the i18n instance directly.
 * `changeLanguage` persists the choice to localStorage automatically (handled by
 * the LanguageDetector cache configured in ./index.ts) and triggers the RTL/LTR
 * document update, so the whole UI updates in real time without a reload.
 *
 * @format
 */

import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { LANGUAGES, getLanguageMeta, type LanguageMeta } from "./config";

export function useLanguage() {
	const { i18n } = useTranslation();
	const current: LanguageMeta = getLanguageMeta(i18n.language);

	const changeLanguage = useCallback(
		(code: string) => {
			if (code !== i18n.language) {
				void i18n.changeLanguage(code);
			}
		},
		[i18n],
	);

	return {
		/** All supported languages, in display order. */
		languages: LANGUAGES,
		/** Metadata for the currently active language. */
		current,
		/** True when the active language is right-to-left (Arabic). */
		isRtl: current.dir === "rtl",
		/** Switch language instantly + persist the choice. */
		changeLanguage,
	};
}
